import './tidyflowmap.css'

import React, { useMemo, useRef, useState } from 'react'
import {
	IonButton,
	IonContent,
	IonFooter,
	IonHeader,
	IonModal,
	IonTitle,
	IonToolbar,
} from '@ionic/react'

import { formatPeriodNumber } from './periodLabels'

type TidyOperation =
	| {
		kind: 'repartition'
		breaks: number[]
	}
	| {
		kind: 'insert'
		quarter: number
		beforeOriginalIndex: number | null
		event: FlowEvent
	}
	| {
		kind: 'replace'
		originalIndex: number
		event: FlowEvent
	}
	| {
		kind: 'remove'
		originalIndex: number
	}

export type TidyFlowFix = {
	id: string
	quarter: number
	description: string
	operations: TidyOperation[]
}

export type TidyFlowPlan = {
	fixes: TidyFlowFix[]
	targetScores: Array<{ home: number; away: number }>
	beforeScores: Array<{ home: number; away: number }>
	afterScores: Array<{ home: number; away: number }>
	isExact: boolean
}

type TaggedEvent = {
	event: FlowEvent
	originalIndex: number | null
}

type TidyFlowMapProps = {
	flowData: FlowData
	result?: Result
	numPeriods: number
	homeName: string
	awayName: string
	disabled?: boolean
	onChange: (flowData: FlowData) => void
}

const cloneEvent = (event: FlowEvent): FlowEvent => ({
	cpTeamCode: event.cpTeamCode,
	turnovers: [ ...event.turnovers ],
	result: event.result,
})

const oppositeSide = (side: FlowTeamSide): FlowTeamSide => side === 'home' ? 'away' : 'home'

const scorerSide = (event: FlowEvent): FlowTeamSide | null => {
	if (event.result === 'X' || event.result === 'I') {
		return null
	}
	return event.turnovers.length % 2 === 0
		? event.cpTeamCode
		: oppositeSide(event.cpTeamCode)
}

const eventPoints = (event: FlowEvent) => event.result === 'GG' ? 2 : scorerSide(event) ? 1 : 0

const attackingZone = (side: FlowTeamSide): FlowEventZone => side === 'home' ? 'A' : 'D'

function resultToPeriodScores(result: Result | undefined, numPeriods: number) {
	if (!result || result.me.length !== numPeriods || result.them.length !== numPeriods) {
		return null
	}

	const scores: Array<{ home: number; away: number }> = []
	let previousHome = 0
	let previousAway = 0
	for (let quarter = 0; quarter < numPeriods; quarter += 1) {
		const cumulativeHome = result.me[quarter]
		const cumulativeAway = result.them[quarter]
		if (
			typeof cumulativeHome !== 'number'
			|| typeof cumulativeAway !== 'number'
			|| !Number.isFinite(cumulativeHome)
			|| !Number.isFinite(cumulativeAway)
			|| cumulativeHome < previousHome
			|| cumulativeAway < previousAway
		) {
			return null
		}
		scores.push({
			home: cumulativeHome - previousHome,
			away: cumulativeAway - previousAway,
		})
		previousHome = cumulativeHome
		previousAway = cumulativeAway
	}
	return scores
}

function splitIntoQuarters(flowData: FlowData, numPeriods: number): TaggedEvent[][] {
	const quarters: TaggedEvent[][] = []
	let start = 0
	for (let quarter = 0; quarter < numPeriods; quarter += 1) {
		const end = Math.min(
			Math.max(flowData.quarterBreaks[quarter] ?? flowData.events.length, start),
			flowData.events.length,
		)
		const quarterStart = start
		quarters.push(flowData.events.slice(quarterStart, end).map((event, offset) => ({
			event: cloneEvent(event),
			originalIndex: quarterStart + offset,
		})))
		start = end
	}
	return quarters
}

function cloneQuarters(quarters: TaggedEvent[][]) {
	return quarters.map(quarter => quarter.map(item => ({
		event: cloneEvent(item.event),
		originalIndex: item.originalIndex,
	})))
}

function applyOperation(quarters: TaggedEvent[][], operation: TidyOperation) {
	if (operation.kind === 'repartition') {
		const flat = quarters.flat()
		const oldQuarterByOriginalIndex = new Map<number, number>()
		quarters.forEach((quarter, quarterIndex) => quarter.forEach(item => {
			if (item.originalIndex !== null) {
				oldQuarterByOriginalIndex.set(item.originalIndex, quarterIndex)
			}
		}))
		const nextQuarters: TaggedEvent[][] = []
		let start = 0
		operation.breaks.forEach((end, quarterIndex) => {
			const nextQuarter = flat.slice(start, end)
			nextQuarter.forEach(item => {
				const oldQuarter = item.originalIndex === null
					? quarterIndex
					: oldQuarterByOriginalIndex.get(item.originalIndex) ?? quarterIndex
				if (oldQuarter % 2 !== quarterIndex % 2) {
					item.event.turnovers = item.event.turnovers.map(zone => (
						zone === 'A' ? 'D' : zone === 'D' ? 'A' : zone
					))
				}
			})
			nextQuarters.push(nextQuarter)
			start = end
		})
		quarters.splice(0, quarters.length, ...nextQuarters)
		return
	}

	if (operation.kind === 'remove') {
		for (const quarter of quarters) {
			const targetIndex = quarter.findIndex(item => item.originalIndex === operation.originalIndex)
			if (targetIndex >= 0) {
				quarter.splice(targetIndex, 1)
				return
			}
		}
		return
	}

	if (operation.kind === 'replace') {
		for (const quarter of quarters) {
			const target = quarter.find(item => item.originalIndex === operation.originalIndex)
			if (target) {
				target.event = cloneEvent(operation.event)
				return
			}
		}
		return
	}

	const quarter = quarters[operation.quarter - 1]
	if (!quarter) {
		return
	}
	const insertionIndex = operation.beforeOriginalIndex === null
		? quarter.length
		: quarter.findIndex(item => item.originalIndex === operation.beforeOriginalIndex)
	quarter.splice(insertionIndex < 0 ? quarter.length : insertionIndex, 0, {
		event: cloneEvent(operation.event),
		originalIndex: null,
	})
}

function scoreRepairCost(
	score: { home: number; away: number },
	target: { home: number; away: number },
) {
	const surplusHome = Math.max(0, score.home - target.home)
	const surplusAway = Math.max(0, score.away - target.away)
	const deficitHome = Math.max(0, target.home - score.home)
	const deficitAway = Math.max(0, target.away - score.away)
	const reassignHome = Math.min(surplusHome, deficitAway)
	const reassignAway = Math.min(surplusAway, deficitHome)
	const reassignments = reassignHome + reassignAway
	const removals = surplusHome + surplusAway - reassignments
	const additions = deficitHome + deficitAway - reassignments
	return (removals * 12) + (additions * 3) + reassignments
}

function calculateSegmentScore(events: FlowEvent[], start: number, end: number) {
	return events.slice(start, end).reduce((score, event) => {
		const scorer = scorerSide(event)
		const points = eventPoints(event)
		if (scorer === 'home') {
			score.home += points
		} else if (scorer === 'away') {
			score.away += points
		}
		return score
	}, { home: 0, away: 0 })
}

function findBetterQuarterBreaks(
	flowData: FlowData,
	targets: Array<{ home: number; away: number }>,
	numPeriods: number,
) {
	const numEvents = flowData.events.length
	if (numPeriods <= 1 || numEvents < numPeriods) {
		return null
	}
	const originalBreaks = [ ...Array(numPeriods - 1).keys() ].map(index => (
		Math.min(flowData.quarterBreaks[index] ?? numEvents, numEvents)
	))
	type BreakState = { cost: number; breaks: number[] }
	let states = new Map<number, BreakState>()
	states.set(0, { cost: 0, breaks: [] })

	for (let quarterIndex = 0; quarterIndex < numPeriods; quarterIndex += 1) {
		const nextStates = new Map<number, BreakState>()
		const minEnd = quarterIndex + 1
		const maxEnd = numEvents - (numPeriods - quarterIndex - 1)
		for (const [ start, state ] of states) {
			for (let end = Math.max(start + 1, minEnd); end <= maxEnd; end += 1) {
				if (quarterIndex === numPeriods - 1 && end !== numEvents) {
					continue
				}
				const segmentScore = calculateSegmentScore(flowData.events, start, end)
				let cost = state.cost + scoreRepairCost(segmentScore, targets[quarterIndex])
				if (quarterIndex < numPeriods - 1) {
					const distance = Math.abs(end - originalBreaks[quarterIndex])
					cost += distance ? 0.5 + (Math.min(distance, 20) * 0.15) : 0
				}
				const existing = nextStates.get(end)
				if (!existing || cost < existing.cost) {
					nextStates.set(end, {
						cost,
						breaks: [ ...state.breaks, end ],
					})
				}
			}
		}
		states = nextStates
	}

	const best = states.get(numEvents)
	if (!best) {
		return null
	}
	const currentCost = splitIntoQuarters(flowData, numPeriods).reduce((cost, quarter, index) => (
		cost + scoreRepairCost(calculatePeriodScores([ quarter ])[0], targets[index])
	), 0)
	const bestBreaks = best.breaks.slice(0, numPeriods - 1)
	if (
		best.cost >= currentCost - 1
		|| bestBreaks.every((value, index) => value === originalBreaks[index])
	) {
		return null
	}
	return {
		breaks: [ ...bestBreaks, numEvents ],
		originalBreaks,
	}
}

function applyFix(quarters: TaggedEvent[][], fix: TidyFlowFix) {
	fix.operations.forEach(operation => applyOperation(quarters, operation))
}

function calculatePeriodScores(quarters: TaggedEvent[][]) {
	return quarters.map(quarter => quarter.reduce((score, item) => {
		const scorer = scorerSide(item.event)
		const points = eventPoints(item.event)
		if (scorer === 'home') {
			score.home += points
		} else if (scorer === 'away') {
			score.away += points
		}
		return score
	}, { home: 0, away: 0 }))
}

function sameScores(
	left: Array<{ home: number; away: number }>,
	right: Array<{ home: number; away: number }>,
) {
	return left.length === right.length && left.every((score, index) => (
		score.home === right[index].home && score.away === right[index].away
	))
}

function countCpViolations(events: FlowEvent[]) {
	let violations = 0
	for (let index = 1; index < events.length; index += 1) {
		if (events[index].cpTeamCode === events[index - 1].cpTeamCode) {
			violations += 1
		}
	}
	return violations
}

function isStructurallyValidTidiedFlow(flowData: FlowData, numPeriods: number) {
	return !!flowData.setup
		&& flowData.quarterBreaks.length === numPeriods
		&& flowData.quarterBreaks.every((value, index) => (
			Number.isInteger(value)
			&& value >= 0
			&& value <= flowData.events.length
			&& (index === 0 || value >= flowData.quarterBreaks[index - 1])
		))
		&& flowData.quarterBreaks[numPeriods - 1] === flowData.events.length
		&& flowData.events.every(event => (
			(event.cpTeamCode === 'home' || event.cpTeamCode === 'away')
			&& (event.result === 'G' || event.result === 'GG' || event.result === 'X' || event.result === 'I')
			&& event.turnovers.every(zone => zone === 'D' || zone === 'M' || zone === 'A')
		))
}

// Arbitrary checkbox combinations can omit an earlier repair that a later one
// assumes. Never allow such a selection to damage a structurally valid flow or
// introduce additional centre-pass violations.
// eslint-disable-next-line react-refresh/only-export-components
export function isTidyFlowSelectionSafe(original: FlowData, candidate: FlowData, numPeriods: number) {
	return isStructurallyValidTidiedFlow(candidate, numPeriods)
		&& countCpViolations(candidate.events) <= countCpViolations(original.events)
}

function describeSide(side: FlowTeamSide, homeName: string, awayName: string) {
	return side === 'home' ? homeName : awayName
}

function describeLateGoal(number: number) {
	if (number === 1) return 'final'
	if (number === 2) return 'second-last'
	if (number === 3) return 'third-last'
	return `${number}th-last`
}

function makeFix(
	fixes: TidyFlowFix[],
	quarters: TaggedEvent[][],
	fix: Omit<TidyFlowFix, 'id'>,
) {
	const completeFix = {
		...fix,
		id: `tidy-${fixes.length + 1}`,
	}
	fixes.push(completeFix)
	applyFix(quarters, completeFix)
}

// The solver is exported from the component module so it can be tested without rendering the UI.
// eslint-disable-next-line react-refresh/only-export-components
export function buildTidyFlowPlan(props: {
	flowData: FlowData
	result?: Result
	numPeriods: number
	homeName?: string
	awayName?: string
}): TidyFlowPlan | null {
	const { flowData, result, numPeriods } = props
	const homeName = props.homeName || 'Home'
	const awayName = props.awayName || 'Away'
	const targets = resultToPeriodScores(result, numPeriods)
	// Older completed recordings may omit the final break: the end of the event
	// array already provides the final period boundary.
	if (!targets || flowData.quarterBreaks.length < Math.max(0, numPeriods - 1)) {
		return null
	}

	const originalQuarters = splitIntoQuarters(flowData, numPeriods)
	const quarters = cloneQuarters(originalQuarters)
	const beforeScores = calculatePeriodScores(originalQuarters)
	const fixes: TidyFlowFix[] = []
	const betterBreaks = findBetterQuarterBreaks(flowData, targets, numPeriods)
	if (betterBreaks) {
		const changes = betterBreaks.breaks.slice(0, -1).flatMap((newBreak, index) => {
			const oldBreak = betterBreaks.originalBreaks[index]
			return oldBreak === newBreak
				? []
				: [ `end of ${formatPeriodNumber(index + 1, numPeriods)} from after chain ${oldBreak} to after chain ${newBreak}` ]
		})
		makeFix(fixes, quarters, {
			quarter: 1,
			description: `Move the period ${changes.length === 1 ? 'break' : 'breaks'} (${changes.join('; ')}), recoding Attack/Defence zones where teams changed ends.`,
			operations: [ {
				kind: 'repartition',
				breaks: betterBreaks.breaks,
			} ],
		})
	}

	// Removing genuine data is the least desirable repair. Only remove a suffix
	// of late chains when that period contains more total points than official;
	// this targets the common case where bogus activity was added to reach End Game.
	for (let quarterIndex = 0; quarterIndex < numPeriods; quarterIndex += 1) {
		const quarter = quarters[quarterIndex]
		const currentScore = calculatePeriodScores(quarters)[quarterIndex]
		const target = targets[quarterIndex]
		if (currentScore.home + currentScore.away <= target.home + target.away) {
			continue
		}
		let best: {
			count: number
			cost: number
		} | null = null
		for (let count = 1; count <= quarter.length; count += 1) {
			const remaining = quarter.slice(0, -count)
			const remainingScore = calculatePeriodScores([ remaining ])[0]
			const cost = scoreRepairCost(remainingScore, target) + (count * 0.75)
			if (!best || cost < best.cost) {
				best = { count, cost }
			}
		}
		const currentCost = scoreRepairCost(currentScore, target)
		if (!best || best.cost >= currentCost) {
			continue
		}
		const removed = quarter.slice(-best.count)
		if (removed.some(item => item.originalIndex === null)) {
			continue
		}
		const removedGoals = removed.filter(item => scorerSide(item.event)).length
		const operations: TidyOperation[] = removed.map(item => ({
			kind: 'remove',
			originalIndex: item.originalIndex!,
		}))
		if (quarterIndex < numPeriods - 1 && removed.length % 2 === 1) {
			operations.push({
				kind: 'insert',
				quarter: quarterIndex + 1,
				beforeOriginalIndex: null,
				event: {
					cpTeamCode: removed[0].event.cpTeamCode,
					turnovers: [],
					result: 'X',
				},
			})
		}
		makeFix(fixes, quarters, {
			quarter: quarterIndex + 1,
			description: `Remove ${removedGoals} late scoring ${removedGoals === 1 ? 'chain' : 'chains'} from ${formatPeriodNumber(quarterIndex + 1, numPeriods)}${removed.length > removedGoals ? ' along with associated unfinished play' : ''}.`,
			operations,
		})
	}

	// A common recorder correction is to change the first CP of the new period
	// after forgetting that a CP was taken immediately before the previous break.
	for (let quarterIndex = 0; quarterIndex < numPeriods - 1; quarterIndex += 1) {
		const quarter = quarters[quarterIndex]
		const nextQuarter = quarters[quarterIndex + 1]
		const last = quarter[quarter.length - 1]
		const next = nextQuarter[0]
		if (
			last
			&& next
			&& (last.event.result === 'G' || last.event.result === 'GG')
			&& next.event.cpTeamCode === last.event.cpTeamCode
		) {
			const cpSide = oppositeSide(last.event.cpTeamCode)
			makeFix(fixes, quarters, {
				quarter: quarterIndex + 1,
				description: `Add the missed ${describeSide(cpSide, homeName, awayName)} centre pass at the end of ${formatPeriodNumber(quarterIndex + 1, numPeriods)}.`,
				operations: [ {
					kind: 'insert',
					quarter: quarterIndex + 1,
					beforeOriginalIndex: null,
					event: { cpTeamCode: cpSide, turnovers: [], result: 'X' },
				} ],
			})
		}
	}

	// A repeated CP within a period is a strong indication that the alternating
	// CP (and its goal) was missed. Only use it when that side is short of goals.
	for (let quarterIndex = 0; quarterIndex < numPeriods; quarterIndex += 1) {
		const quarter = quarters[quarterIndex]
		let index = 1
		while (index < quarter.length) {
			const previous = quarter[index - 1]
			const next = quarter[index]
			const currentScore = calculatePeriodScores(quarters)[quarterIndex]
			const missingSide = oppositeSide(previous.event.cpTeamCode)
			const missingGoals = targets[quarterIndex][missingSide] - currentScore[missingSide]
			const isMissingTotal = currentScore.home + currentScore.away
				< targets[quarterIndex].home + targets[quarterIndex].away
			if (
				(previous.event.result === 'G' || previous.event.result === 'GG')
				&& next.event.cpTeamCode === previous.event.cpTeamCode
				&& missingGoals > 0
				&& isMissingTotal
			) {
				makeFix(fixes, quarters, {
					quarter: quarterIndex + 1,
					description: `Add a missed ${describeSide(missingSide, homeName, awayName)} goal before the repeated centre pass in ${formatPeriodNumber(quarterIndex + 1, numPeriods)}.`,
					operations: [ {
						kind: 'insert',
						quarter: quarterIndex + 1,
						beforeOriginalIndex: next.originalIndex,
						event: { cpTeamCode: missingSide, turnovers: [], result: 'G' },
					} ],
				})
				index += 2
				continue
			}
			index += 1
		}
	}

	// Prefer completing an already-recorded final possession to inventing a new
	// scoring chain. Add one turnover if the official scorer is the other side.
	for (let quarterIndex = 0; quarterIndex < numPeriods; quarterIndex += 1) {
		const quarter = quarters[quarterIndex]
		const last = quarter[quarter.length - 1]
		if (!last || last.event.result !== 'X' || last.originalIndex === null) {
			continue
		}
		const score = calculatePeriodScores(quarters)[quarterIndex]
		const deficits = {
			home: targets[quarterIndex].home - score.home,
			away: targets[quarterIndex].away - score.away,
		}
		if (
			targets[quarterIndex].home + targets[quarterIndex].away
			<= score.home + score.away
		) {
			continue
		}
		const desiredSide: FlowTeamSide | null = deficits.home > 0
			? 'home'
			: deficits.away > 0 ? 'away' : null
		if (!desiredSide) {
			continue
		}
		const possession = last.event.turnovers.length % 2 === 0
			? last.event.cpTeamCode
			: oppositeSide(last.event.cpTeamCode)
		const replacement = cloneEvent(last.event)
		if (possession !== desiredSide) {
			replacement.turnovers.push(attackingZone(possession))
		}
		replacement.result = 'G'
		makeFix(fixes, quarters, {
			quarter: quarterIndex + 1,
			description: `Complete the final possession in ${formatPeriodNumber(quarterIndex + 1, numPeriods)} as a ${describeSide(desiredSide, homeName, awayName)} goal.`,
			operations: [ {
				kind: 'replace',
				originalIndex: last.originalIndex,
				event: replacement,
			} ],
		})
	}

	// When the right number of goals exists but they are assigned to the wrong
	// side, work backwards and append an inferred turnover to the latest goals.
	for (let quarterIndex = 0; quarterIndex < numPeriods; quarterIndex += 1) {
		let reassignmentNumber = 0
		for (let index = quarters[quarterIndex].length - 1; index >= 0; index -= 1) {
			const score = calculatePeriodScores(quarters)[quarterIndex]
			const surplusSide: FlowTeamSide | null = score.home > targets[quarterIndex].home
				&& score.away < targets[quarterIndex].away
				? 'home'
				: score.away > targets[quarterIndex].away && score.home < targets[quarterIndex].home
					? 'away'
					: null
			if (!surplusSide) {
				break
			}
			const item = quarters[quarterIndex][index]
			if (item.originalIndex === null || scorerSide(item.event) !== surplusSide) {
				continue
			}
			const points = eventPoints(item.event)
			const deficitSide = oppositeSide(surplusSide)
			if (
				score[surplusSide] - targets[quarterIndex][surplusSide] < points
				|| targets[quarterIndex][deficitSide] - score[deficitSide] < points
			) {
				continue
			}
			const replacement = cloneEvent(item.event)
			replacement.turnovers.push(attackingZone(surplusSide))
			reassignmentNumber += 1
			const which = describeLateGoal(reassignmentNumber)
			makeFix(fixes, quarters, {
				quarter: quarterIndex + 1,
				description: `Reassign the ${which} recorded ${describeSide(surplusSide, homeName, awayName)} goal in ${formatPeriodNumber(quarterIndex + 1, numPeriods)} to ${describeSide(deficitSide, homeName, awayName)}, with an inferred turnover.`,
				operations: [ {
					kind: 'replace',
					originalIndex: item.originalIndex,
					event: replacement,
				} ],
			})
		}
	}

	// Fill remaining shortages at the end of a period. Invent all required goals
	// as one fix, then add an unfinished CP when needed to preserve the next
	// period's already-recorded CP phase.
	for (let quarterIndex = 0; quarterIndex < numPeriods; quarterIndex += 1) {
		const score = calculatePeriodScores(quarters)[quarterIndex]
		const deficits = {
			home: Math.max(0, targets[quarterIndex].home - score.home),
			away: Math.max(0, targets[quarterIndex].away - score.away),
		}
		const quarter = quarters[quarterIndex]
		const last = quarter[quarter.length - 1]
		if (
			(!deficits.home && !deficits.away)
			|| last?.event.result === 'X'
			|| last?.event.result === 'I'
		) {
			continue
		}

		const operations: TidyOperation[] = []
		const previousLast = quarters[quarterIndex - 1]?.[quarters[quarterIndex - 1].length - 1]
		let cpSide = last
			? oppositeSide(last.event.cpTeamCode)
			: previousLast ? oppositeSide(previousLast.event.cpTeamCode) : flowData.setup.firstCpTeam
		let addedHome = 0
		let addedAway = 0
		while ((deficits.home > 0 || deficits.away > 0) && operations.length < 50) {
			const desiredSide: FlowTeamSide = deficits.home > 0 ? 'home' : 'away'
			const turnovers: FlowEventZone[] = cpSide === desiredSide ? [] : [ attackingZone(cpSide) ]
			operations.push({
				kind: 'insert',
				quarter: quarterIndex + 1,
				beforeOriginalIndex: null,
				event: { cpTeamCode: cpSide, turnovers, result: 'G' },
			})
			deficits[desiredSide] -= 1
			if (desiredSide === 'home') {
				addedHome += 1
			} else {
				addedAway += 1
			}
			cpSide = oppositeSide(cpSide)
		}

		const nextFirst = quarters[quarterIndex + 1]?.[0]?.event
		if (nextFirst && nextFirst.cpTeamCode !== cpSide) {
			operations.push({
				kind: 'insert',
				quarter: quarterIndex + 1,
				beforeOriginalIndex: null,
				event: { cpTeamCode: cpSide, turnovers: [], result: 'X' },
			})
		}

		const additions = [
			addedHome ? `${addedHome} ${homeName}` : '',
			addedAway ? `${addedAway} ${awayName}` : '',
		].filter(Boolean).join(' and ')
		makeFix(fixes, quarters, {
			quarter: quarterIndex + 1,
			description: `Add ${additions} missing ${addedHome + addedAway === 1 ? 'goal' : 'goals'} at the end of ${formatPeriodNumber(quarterIndex + 1, numPeriods)}.`,
			operations,
		})
	}

	const afterScores = calculatePeriodScores(quarters)
	return {
		fixes,
		targetScores: targets,
		beforeScores,
		afterScores,
		isExact: sameScores(afterScores, targets),
	}
}

// eslint-disable-next-line react-refresh/only-export-components
export function applyTidyFlowFixes(flowData: FlowData, numPeriods: number, fixes: TidyFlowFix[]) {
	const quarters = splitIntoQuarters(flowData, numPeriods)
	fixes.forEach(fix => applyFix(quarters, fix))
	const events: FlowEvent[] = []
	const quarterBreaks: number[] = []
	quarters.forEach(quarter => {
		quarter.forEach(item => events.push(cloneEvent(item.event)))
		quarterBreaks.push(events.length)
	})
	const tidiedFlow = {
		...flowData,
		events,
		quarterBreaks,
		draftEvent: null,
	}
	if (!isStructurallyValidTidiedFlow(tidiedFlow, numPeriods)) {
		console.error('Tidy produced invalid Flow Map data; preserving the original recording.')
		return {
			...flowData,
			events: flowData.events.map(cloneEvent),
			quarterBreaks: [ ...flowData.quarterBreaks ],
		}
	}
	return tidiedFlow
}

function scoreLine(scores: Array<{ home: number; away: number }>) {
	return scores.map(score => `${score.home}-${score.away}`).join(', ')
}

const TidyFlowMap = (props: TidyFlowMapProps) => {
	const { flowData, result, numPeriods, homeName, awayName, disabled, onChange } = props
	const plan = useMemo(() => buildTidyFlowPlan({
		flowData,
		result,
		numPeriods,
		homeName,
		awayName,
	}), [ awayName, flowData, homeName, numPeriods, result ])
	const [ isOpen, setIsOpen ] = useState(false)
	const [ selectedFixIds, setSelectedFixIds ] = useState<Set<string>>(new Set())
	const undoFlowRef = useRef<FlowData | null>(null)

	if (!plan || (!plan.fixes.length && !undoFlowRef.current)) {
		return null
	}

	const open = () => {
		setSelectedFixIds(new Set(plan.fixes.map(fix => fix.id)))
		setIsOpen(true)
	}
	const selectedFixes = plan.fixes.filter(fix => selectedFixIds.has(fix.id))
	const displayedFixes = plan.fixes
		.map((fix, solverIndex) => ({ fix, solverIndex }))
		.sort((left, right) => {
			const leftIsStructural = left.fix.operations.some(operation => operation.kind === 'repartition')
			const rightIsStructural = right.fix.operations.some(operation => operation.kind === 'repartition')
			if (leftIsStructural !== rightIsStructural) {
				return leftIsStructural ? -1 : 1
			}
			return left.fix.quarter - right.fix.quarter || left.solverIndex - right.solverIndex
		})
		.map(({ fix }) => fix)
	const previewFlow = applyTidyFlowFixes(flowData, numPeriods, selectedFixes)
	const previewScores = calculatePeriodScores(splitIntoQuarters(previewFlow, numPeriods))
	const previewIsExact = sameScores(previewScores, plan.targetScores)
	const previewIsSafe = isTidyFlowSelectionSafe(flowData, previewFlow, numPeriods)

	const apply = () => {
		if (!selectedFixes.length || !previewIsSafe) {
			return
		}
		undoFlowRef.current = {
			...flowData,
			events: flowData.events.map(cloneEvent),
			quarterBreaks: [ ...flowData.quarterBreaks ],
		}
		onChange(previewFlow)
		setIsOpen(false)
	}

	const undo = () => {
		if (!undoFlowRef.current) {
			return
		}
		onChange(undoFlowRef.current)
		undoFlowRef.current = null
		setIsOpen(false)
	}

	return (
		<div className="tidy-flow-map">
			<button type="button" className="tidy-flow-map-open" onClick={open} disabled={disabled}>
				Tidy data
			</button>
			<IonModal
				className="tidy-flow-map-modal"
				isOpen={isOpen}
				onDidDismiss={() => setIsOpen(false)}
			>
				<IonHeader>
					<IonToolbar>
						<IonTitle>Tidy Flow Map Data</IonTitle>
						<IonButton slot="end" fill="clear" onClick={() => setIsOpen(false)}>
							Cancel
						</IonButton>
					</IonToolbar>
				</IonHeader>
				<IonContent>
					<div className="tidy-flow-map-panel">
						{plan.fixes.length ? (
							<>
                                <p>
                                    AutoRoster can automatically fix recording errors in your Flow Map data.
                                </p>
								<table className="tidy-flow-map-scores">
									<thead>
										<tr>
											<th scope="col" aria-label="Period" />
											<th scope="col">Official</th>
											<th scope="col">Flow Map</th>
										</tr>
									</thead>
									<tbody>
										{plan.targetScores.map((official, index) => {
											const recorded = plan.beforeScores[index]
											const matches = official.home === recorded.home && official.away === recorded.away
											return (
												<tr key={index}>
													<th scope="row">{formatPeriodNumber(index + 1, numPeriods)}</th>
													<td>{official.home}-{official.away}</td>
													<td className={matches ? 'matches' : 'mismatch'}>
														{recorded.home}-{recorded.away}
														<span
															className="tidy-flow-map-score-status"
															role="img"
															aria-label={matches ? 'Matches official score' : 'Does not match official score'}
														>
															{matches ? '✓' : '✕'}
														</span>
													</td>
												</tr>
											)
										})}
									</tbody>
								</table>
								<div className="tidy-flow-map-fixes">
									{displayedFixes.map(fix => (
										<label key={fix.id}>
											<input
												type="checkbox"
												checked={selectedFixIds.has(fix.id)}
												onChange={event => setSelectedFixIds(current => {
													const next = new Set(current)
													if (event.target.checked) {
														next.add(fix.id)
													} else {
														next.delete(fix.id)
													}
													return next
												})}
											/>
											<span>{fix.description}</span>
										</label>
									))}
								</div>
								<p className={previewIsExact ? 'tidy-flow-map-match' : 'tidy-flow-map-warning'}>
									After fixes: {scoreLine(previewScores)}. {previewIsExact
										? 'This matches the official scores.'
										: 'This does not fully match the official scores.'}
								</p>
								{!previewIsSafe && (
									<p className="tidy-flow-map-warning">
										This combination of fixes would disrupt centre-pass alternation. Select a compatible set of fixes.
									</p>
								)}
							</>
						) : (
							<p>Data tidied! No more fixes are suggested.</p>
						)}
					</div>
				</IonContent>
				<IonFooter>
					<IonToolbar>
						{undoFlowRef.current && (
							<IonButton slot="start" fill="clear" onClick={undo} disabled={disabled}>
								Undo Tidy
							</IonButton>
						)}
						{plan.fixes.length > 0 && (
							<IonButton slot="end" onClick={apply} disabled={!selectedFixes.length || !previewIsSafe}>
								Apply selected fixes
							</IonButton>
						)}
					</IonToolbar>
				</IonFooter>
			</IonModal>
		</div>
	)
}

export default TidyFlowMap
